import { useToast, Text, Box, Heading, Accordion } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { AthenaTableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { FinleyAiViewContext } from './types';
import { useGetAthenaTablesForOrganizationQuery } from 'packages/athena/requests/requests.gql.generated';
import { AthenaTable } from 'packages/athena/ui/AthenaTable';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

type FinleyAiAthenaSqlGenerationProps = {
  targetOrganizationId: string;
  selectedTableNames: string[];
  isLocked: boolean;
  viewContext: FinleyAiViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
};

export const FinleyAiAthenaSqlGeneration = ({
  targetOrganizationId,
  selectedTableNames,
  isLocked,
  viewContext,
  setViewContext,
}: FinleyAiAthenaSqlGenerationProps) => {
  const toast = useToast();

  const { data: athenaTablesData } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId: targetOrganizationId },
  });

  const onToggleTableSelect = useCallback(
    (tableInput: AthenaTableGQLType) => {
      finleyAssert(
        viewContext.type === 'athenaSqlGeneration',
        'Unable to set table selection.',
      );

      if (isLocked) {
        toast({
          title: 'Start a new thread to change tables.',
          position: 'top',
          duration: 6000,
          isClosable: true,
          status: 'info',
        });
        return;
      }
      const isTableSelected = selectedTableNames.some(
        (tableName) => tableInput.tableName === tableName,
      );

      const newTableNameSelections = isTableSelected
        ? selectedTableNames.filter(
            (tableName) => tableInput.tableName !== tableName,
          )
        : selectedTableNames.concat([tableInput.tableName]);

      setViewContext((prevState) => ({
        ...prevState,
        tableNames: newTableNameSelections,
      }));
    },
    [isLocked, selectedTableNames, toast, viewContext, setViewContext],
  );

  if (!athenaTablesData) {
    return <Text>Loading database tables...</Text>;
  }

  const athenaTables = athenaTablesData.athenaTables;

  return (
    <Box mr="20px">
      <Heading as="h4" size="md" mb="3">
        Tables
      </Heading>
      <Accordion allowToggle w="xl">
        {athenaTables.map((table) => {
          const isSelected = selectedTableNames.some(
            (tableName) => tableName === table.tableName,
          );
          return (
            <AthenaTable
              key={table.tableName}
              table={table}
              isSelected={isSelected}
              onToggleTableSelect={onToggleTableSelect}
              checkBoxPosition="left"
            />
          );
        })}
      </Accordion>
    </Box>
  );
};
